import { StrictMode, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from "react-router";
import { createRoot } from "react-dom/client";
import Cookies from "universal-cookie";

// Components
import HomePage from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import { I18nextProvider } from "react-i18next";

// internationalization
import global_en from "@/translation/En/global.json";
import global_es from "@/translation/Es/global.json";
import business_en from "@/translation/En/business.json";
import business_es from "@/translation/Es/business.json";
import cabo_en from "@/translation/En/cabo.json";
import cabo_es from "@/translation/Es/cabo.json";
import errors_en from "@/translation/En/errors.json";
import errors_es from "@/translation/Es/errors.json";
import i18next from "i18next";

import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { SupportButton } from "@/components/SupportButton";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import Faqs from "./pages/Faqs";

// pages
import Legal from "@/pages/Legal";
import HowItWorksPage from "@/pages/how-it-works";
import ForPartnersPage from "@/pages/for-partners";
import ClaroPay from "@/pages/ClaroPay";
import { CaboHeader } from "./components/CaboHeader";
import CaboPage from "./pages/Cabo/Cabo.page";
import MarisolPage from "@/pages/Marisol";

const cookies = new Cookies();

const DEFAULT_LANGUAGE = "en";
const BusinessSiteUrl = import.meta.env.VITE_BUSINESS_SITE_URL;

i18next.init({
  interpolation: { escapeValue: false },
  lng: cookies.get("lang") || DEFAULT_LANGUAGE,
  resources: {
    es: {
      global: global_es,
      business: business_es,
      cabo: cabo_es,
      errors: errors_es,
    },
    en: {
      global: global_en,
      business: business_en,
      cabo: cabo_en,
      errors: errors_en,
    },
  },
});

const Layout = () => {
  const location = useLocation();
  const pathname = location?.pathname;

  useEffect(() => {
    scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="flex-1 flex">
        <Outlet />
      </div>
      <SupportButton />
      <Footer />
    </div>
  );
};

const ExternalBusinessRedirect = () => {
  useEffect(() => {
    window.location.replace(BusinessSiteUrl);
  }, []);
  return null;
};

const CaboLayout = () => {
  const location = useLocation();
  const pathname = location?.pathname;

  useEffect(() => {
    scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <CaboHeader />

      <div className="flex-1 flex">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        path: "",
        element: <HomePage />,
      },
      {
        path: "/legal",
        element: <Legal />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
      {
        path: "/faqs",
        element: <Faqs />,
      },
      {
        path: "/how-it-works",
        element: <HowItWorksPage />,
      },
      {
        path: "/for-partners",
        element: <Navigate to="/become-a-partner" />,
      },
      {
        path: "/become-a-partner",
        element: <ForPartnersPage />,
      },
      {
        path: "/about-us",
        element: <Navigate to="/" />,
      },
      {
        path: "/sendola-corp",
        element: <Navigate to="/" />,
      },
      // {
      //   path: "/sendola-corp",
      //   element: <AboutUsPage />,
      // },
      {
        path: "/claropay",
        element: <ClaroPay />,
      },
      {
        path: "/claropay-send",
        element: <Navigate to="/claropay" />,
      },
      {
        path: "/msfcu-bank-account",
        element: <MarisolPage />,
      },
    ],
  },
  {
    path: "/cabo",
    element: <CaboLayout />,
    children: [
      {
        index: true,
        path: "",
        element: <CaboPage />,
      },
    ],
  },
  {
    path: "/business",
    children: [
      {
        index: true,
        path: "",
        element: <ExternalBusinessRedirect />,
      },
      {
        path: "contact",
        element: <ExternalBusinessRedirect />,
      },
      {
        path: "*",
        element: <ExternalBusinessRedirect />,
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <I18nextProvider i18n={i18next}>
        <RouterProvider router={router} />
      </I18nextProvider>
    </HelmetProvider>
  </StrictMode>
);
